"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { motion } from "motion/react";
import { Camera, Video, Play } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { FadeInSection } from "@/components/common/FadeInSection";
import { cleanHeaderTags, createLookupObject, isValidImgSrc } from "@/_helper/helper";
import { useAppSelector } from "@/store/hooks";
import { Banner } from "../common/Banner";

// Helper to extract YouTube ID
const getYouTubeId = (url: string) => {
  if (!url) return null;
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

export default function GalleryClient({
  galleryData,
  cmsData,
  initialBanner,
}: {
  galleryData: { Gallery: any[]; VideoGallery: any[] };
  cmsData: any;
  initialBanner?: any;
}) {
  const BannerList = useAppSelector((state) => state.home.BannerList);

  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState<"images" | "videos">("images");
  const [selectedCategory, setSelectedCategory] =
    useState<string>("All Images");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);

  const videoGridRef = useRef<HTMLDivElement>(null);

  const banner: any =
    initialBanner ||
    BannerList?.find((banner: any) => banner?.BannerType === "gallery") || {};

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  // Stop video when clicking outside or switching tabs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        videoGridRef.current &&
        !videoGridRef.current.contains(event.target as Node)
      ) {
        setPlayingVideo(null);
      }
    };

    if (playingVideo) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [playingVideo]);

  useEffect(() => {
    setPlayingVideo(null);
  }, [activeTab]);

  // Dynamic categories from Gallery data
  const dynamicCategories = useMemo(() => {
    const types = new Set(
      (galleryData?.Gallery || []).map((item: any) => item.GalleryType),
    );
    return ["All Images", ...Array.from(types).filter(Boolean)];
  }, [galleryData?.Gallery]);

  // Map API Gallery to display format
  const displayGallery = useMemo(() => {
    return (galleryData?.Gallery || []).map((item: any) => ({
      src: item.GalleryImage,
      category: item.GalleryType || "Other",
      title: item.ImgAlt || "Sarasiruham Resort",
    }));
  }, [galleryData?.Gallery]);

  const filteredImages = useMemo(() => {
    return selectedCategory === "All Images"
      ? displayGallery
      : displayGallery.filter((img: any) => img.category === selectedCategory);
  }, [selectedCategory, displayGallery]);

  const lightboxSlides = filteredImages.map((img: any) => ({ src: img.src }));

  // Map API VideoGallery to display format
  const displayVideos = (galleryData?.VideoGallery || []).map((video: any) => {
    const videoId = getYouTubeId(video.VideoURL);
    return {
      videoId: videoId,
      url: video.VideoURL,
      thumbnail: videoId
        ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        : "/imports/placeholder.png",
      title: video.Title || "Sarasiruham Resort Video",
    };
  });

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      {/* Hero */}
      <Banner
        title={banner?.BannerTitle || "Resort Gallery"}
        description={banner?.Description}
        bgImage={banner?.BannerImage}
        // icon={<Camera className="w-12 h-12 text-[#d4af37]" />}
      />
      {/* <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 z-10" />
        <Image
          src="https://images.unsplash.com/photo-1638904998527-a451c1fbd1cb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1920"
          alt="Gallery"
          fill
          className="object-cover"
        />

        <div className="absolute inset-0 z-20 flex items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Camera className="w-12 h-12 text-[#d4af37] mx-auto mb-6" />
            <h1
              className="text-5xl md:text-7xl text-white mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 400,
              }}
            >
              Our{" "}
              <span style={{ fontWeight: 600, fontStyle: "italic" }}>
                Gallery
              </span>
            </h1>
            <p
              className="text-xl text-white/90"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Discover the beauty and elegance of Sarasiruham Resort
            </p>
          </motion.div>
        </div>
      </section> */}

      {/* Tab Navigation */}
      <section className="bg-white border-b border-[#5a6f3e]/10 sticky top-[75px] z-40">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                setActiveTab("images");
                window.scrollTo({ top: 600, behavior: "smooth" });
              }}
              className={`flex items-center gap-2 px-8 py-3 transition-all cursor-pointer ${
                activeTab === "images"
                  ? "bg-[#5a6f3e] text-white shadow-lg"
                  : "bg-[#f5f1ec] text-[#2a2520] hover:bg-[#e8dfd0]"
              }`}
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 500 }}
            >
              <Camera className="w-5 h-5" />
              Image Gallery
            </button>

            <button
              onClick={() => {
                setActiveTab("videos");
                window.scrollTo({ top: 500, behavior: "smooth" });
              }}
              className={`flex items-center gap-2 px-8 py-3 transition-all cursor-pointer ${
                activeTab === "videos"
                  ? "bg-[#5a6f3e] text-white shadow-lg"
                  : "bg-[#f5f1ec] text-[#2a2520] hover:bg-[#e8dfd0]"
              }`}
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 500 }}
            >
              <Video className="w-5 h-5" />
              Video Gallery
            </button>
          </div>
        </div>
      </section>

      {/* Images Tab */}
      {activeTab === "images" && (
        <>
          {/* Category Filter */}
          <section className="py-12 bg-[#f5f1ec]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-wrap items-center justify-center gap-3">
                {dynamicCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-6 py-2.5 transition-all cursor-pointer ${
                      selectedCategory === category
                        ? "bg-[#5a6f3e] text-white shadow-lg"
                        : "bg-white text-[#2a2520] hover:bg-[#fdfcfa]"
                    }`}
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 500 }}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Image Grid */}
          <section className="py-16 bg-[#fdfcfa]">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredImages.map((image: any, i: number) => (
                  <FadeInSection key={i} delay={i * 0.05}>
                    <motion.div
                      whileHover={{
                        y: -8,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                      }}
                      transition={{ duration: 0.4 }}
                      onClick={() => {
                        setPhotoIndex(i);
                        setLightboxOpen(true);
                      }}
                      className="relative overflow-hidden h-72 shadow-lg cursor-pointer group"
                    >
                      <Image
                        src={
                          isValidImgSrc(image.src)
                            ? image.src
                            : "/imports/placeholder.png"
                        }
                        alt={image.title}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                        className="object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                        <div>
                          <p
                            className="text-white text-lg"
                            style={{
                              fontFamily: "Cormorant Garamond, serif",
                              fontWeight: 600,
                            }}
                          >
                            {image.title}
                          </p>
                          <p
                            className="text-white/80 text-sm"
                            style={{ fontFamily: "Jost, sans-serif" }}
                          >
                            {image.category}
                          </p>
                        </div>
                      </div>

                      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera className="w-5 h-5 text-white" />
                      </div>
                    </motion.div>
                  </FadeInSection>
                ))}
              </div>
            </div>
          </section>

          {/* Lightbox */}
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            slides={lightboxSlides}
            index={photoIndex}
            styles={{ slide: { padding: 0 } }}
            render={{
              slide: ({ slide }) => (
                <div className="relative w-[90%] h-full flex items-center justify-center">
                  <Image
                    src={slide.src || ""}
                    alt={"Gallery Image " + (photoIndex + 1)}
                    fill
                    sizes="(max-width: 768px) 100vw, 90vw"
                    className="object-contain pointer-events-none"
                  />
                </div>
              ),
            }}
          />
        </>
      )}

      {/* Videos Tab */}
      {activeTab === "videos" && (
        <section className="py-24 bg-[#fdfcfa]">
          <div className="max-w-7xl mx-auto px-6" ref={videoGridRef}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayVideos.map((video, i) => (
                <FadeInSection key={i} delay={i * 0.1}>
                  <motion.div
                    whileHover={
                      !playingVideo
                        ? {
                            y: -10,
                            boxShadow: "0 25px 50px rgba(0,0,0,0.2)",
                          }
                        : {}
                    }
                    transition={{ duration: 0.4 }}
                    className="relative overflow-hidden h-80 shadow-lg group bg-black"
                  >
                    {playingVideo === video.videoId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
                        title={video.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <>
                        <Image
                          src={video.thumbnail}
                          alt={video.title}
                          fill
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          className="object-cover group-hover:scale-110 transition-transform duration-500"
                        />

                        <div
                          className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center cursor-pointer"
                          onClick={() => setPlayingVideo(video.videoId)}
                        >
                          <motion.div
                            whileHover={{ scale: 1.2 }}
                            className="bg-[#d4af37] text-[#2a2520] p-6 rounded-full shadow-2xl"
                          >
                            <Play className="w-10 h-10" fill="#2a2520" />
                          </motion.div>
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6 pointer-events-none">
                          <h3
                            className="text-white text-xl mb-2"
                            style={{
                              fontFamily: "Cormorant Garamond, serif",
                              fontWeight: 600,
                            }}
                          >
                            {video.title}
                          </h3>
                        </div>
                      </>
                    )}
                  </motion.div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {lookUpCMSData?.["Gallery - Section 1 : CTA"] && (
        <section className="py-24 bg-gradient-to-br from-[#5a6f3e] to-[#4a5f2e] text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <FadeInSection>
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    lookUpCMSData?.["Gallery - Section 1 : CTA"]?.Template,
                  ),
                }}
              />
              {/* <h2
              className="text-4xl md:text-5xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 400,
                }}
            >
            Ready to{" "}
            <span style={{ fontWeight: 600, fontStyle: "italic" }}>
            Experience This?
            </span>
            </h2>
            <p
            className="text-xl text-white/90 mb-12"
            style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
            Book your stay and create your own memorable moments at
            Sarasiruham Resort
            </p> */}

              <Link href={"/contact"}>
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 20px 40px rgba(212, 175, 55, 0.4)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#d4af37] text-[#2a2520] px-14 py-5 text-lg shadow-2xl cursor-pointer"
                  style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                >
                  Book Your Stay
                </motion.button>
              </Link>
            </FadeInSection>
          </div>
        </section>
      )}
    </div>
  );
}
